import React, { useState } from 'react';
import { MapPin, Navigation, AlertTriangle, Clock, Phone, Send, User, Users } from 'lucide-react';

interface Supplier {
  id: string;
  name: string;
  role: string;
  status: 'on-site' | 'en-route' | 'unknown' | 'wrong-site';
  eta?: string;
  distance?: string;
  lastUpdate?: string;
  phone?: string;
  isSharing: boolean;
}

interface LocationDashboardProps {
  suppliers: Supplier[];
  onAction?: (supplierId: string, action: string) => void;
}

type FilterType = 'all' | 'on-site' | 'en-route' | 'wrong-site' | 'unknown';

export const LocationDashboard: React.FC<LocationDashboardProps> = ({ suppliers, onAction }) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Count statuses
  const statusCounts = {
    all: suppliers.length,
    'on-site': suppliers.filter(s => s.status === 'on-site').length,
    'en-route': suppliers.filter(s => s.status === 'en-route').length,
    'wrong-site': suppliers.filter(s => s.status === 'wrong-site').length,
    'unknown': suppliers.filter(s => s.status === 'unknown').length
  };

  // Filter suppliers based on active filter
  const filteredSuppliers = activeFilter === 'all' 
    ? suppliers 
    : suppliers.filter(s => s.status === activeFilter);

  // Sort filtered suppliers by priority
  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    const priority: { [key: string]: number } = {
      'wrong-site': 0,
      'en-route': 1,
      'on-site': 2,
      'unknown': 3
    };
    return priority[a.status] - priority[b.status];
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on-site': return <MapPin className="w-4 h-4 text-green-600" />;
      case 'en-route': return <Navigation className="w-4 h-4 text-blue-600" />;
      case 'wrong-site': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default: return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'on-site': return 'On Site';
      case 'en-route': return 'En Route';
      case 'wrong-site': return 'Wrong Location';
      default: return 'Location Unknown';
    }
  };

  const FilterButton: React.FC<{
    filter: FilterType;
    label: string;
    count: number;
    icon?: React.ReactNode;
    color?: string;
  }> = ({ filter, label, count, icon, color }) => {
    const isActive = activeFilter === filter;
    const hasAlert = filter === 'wrong-site' && count > 0;
    
    return (
      <button
        onClick={() => setActiveFilter(filter)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          backgroundColor: isActive ? (color || '#e8f5e9') : 'transparent',
          border: isActive ? `1px solid ${color || '#2e7d32'}` : '1px solid transparent',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: isActive ? 500 : 400,
          color: isActive ? (filter === 'wrong-site' ? '#d93025' : '#2e7d32') : 
                 hasAlert ? '#d93025' : '#6b7280',
          cursor: 'pointer',
          transition: 'all 0.2s',
          position: 'relative',
          ...(hasAlert && !isActive && {
            animation: 'pulse 2s infinite'
          })
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.border = '1px solid #dadce0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.border = '1px solid transparent';
          }
        }}
      >
        {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
        <span>{count} {label}</span>
        {hasAlert && !isActive && (
          <span style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            width: '8px',
            height: '8px',
            backgroundColor: '#d93025',
            borderRadius: '50%',
            animation: 'pulse 1s infinite'
          }} />
        )}
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px 24px',
        background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}>
            <MapPin size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Location Tracking</h3>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Real-time supplier location & ETA monitoring</div>
          </div>
        </div>
        
        {/* Header Stats */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            <MapPin size={14} />
            <span>{statusCounts['on-site']} On Site</span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            <Navigation size={14} />
            <span>{statusCounts['en-route']} En Route</span>
          </div>
          {statusCounts['wrong-site'] > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <AlertTriangle size={14} />
              <span>{statusCounts['wrong-site']} Wrong Site</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
      {/* Filter Bar */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        marginBottom: '20px',
        padding: '16px 20px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        flexWrap: 'wrap'
      }}>
        <FilterButton 
          filter="all" 
          label="All Suppliers" 
          count={statusCounts.all}
          icon={<Users className="w-4 h-4" />}
        />
        <FilterButton 
          filter="on-site" 
          label="On Site" 
          count={statusCounts['on-site']}
          icon={<MapPin className="w-4 h-4" style={{ color: '#188038' }} />}
          color="#e6f4ea"
        />
        <FilterButton 
          filter="en-route" 
          label="En Route" 
          count={statusCounts['en-route']}
          icon={<Navigation className="w-4 h-4" style={{ color: '#1a73e8' }} />}
          color="#e8f0fe"
        />
        {statusCounts['wrong-site'] > 0 && (
          <FilterButton 
            filter="wrong-site" 
            label="Wrong Site!" 
            count={statusCounts['wrong-site']}
            icon={<AlertTriangle className="w-4 h-4" style={{ color: '#d93025' }} />}
            color="#fce8e6"
          />
        )}
        <FilterButton 
          filter="unknown" 
          label="Unknown" 
          count={statusCounts['unknown']}
          icon={<User className="w-4 h-4" style={{ color: '#5f6368' }} />}
          color="#f8f9fa"
        />
      </div>

      {/* Active Filter Label */}
      {activeFilter !== 'all' && (
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ 
            fontSize: '13px', 
            color: '#5f6368',
            fontWeight: 500
          }}>
            Showing {filteredSuppliers.length} {activeFilter.replace('-', ' ')} supplier{filteredSuppliers.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setActiveFilter('all')}
            style={{
              fontSize: '13px',
              color: '#2e7d32',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Supplier Cards Grid */}
      {sortedSuppliers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#5f6368'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>
            No suppliers found
          </div>
          <div style={{ fontSize: '14px' }}>
            No suppliers match the selected filter
          </div>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {sortedSuppliers.map((supplier) => (
          <div
            key={supplier.id}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '16px',
              transition: 'box-shadow 0.2s',
              cursor: 'pointer',
              position: 'relative',
              ...(supplier.status === 'wrong-site' && {
                borderColor: '#d93025',
                backgroundColor: '#fef1f0'
              })
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px 1px rgba(60,64,67,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Header with Icon and Name */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getStatusIcon(supplier.status)}
                <div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    color: '#202124',
                    lineHeight: '20px'
                  }}>
                    {supplier.name}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#5f6368',
                    marginTop: '2px'
                  }}>
                    {supplier.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Status and Info */}
            <div style={{ 
              fontSize: '14px',
              color: '#5f6368',
              marginBottom: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <span style={{ 
                  fontWeight: 500,
                  color: supplier.status === 'wrong-site' ? '#d93025' : 
                         supplier.status === 'en-route' ? '#1a73e8' :
                         supplier.status === 'on-site' ? '#188038' : '#5f6368'
                }}>
                  {getStatusLabel(supplier.status)}
                </span>
                {supplier.eta && supplier.status === 'en-route' && (
                  <>
                    <span>•</span>
                    <span>ETA {supplier.eta}</span>
                  </>
                )}
                {supplier.distance && supplier.status === 'en-route' && (
                  <>
                    <span>•</span>
                    <span>{supplier.distance}</span>
                  </>
                )}
              </div>
              {supplier.lastUpdate && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  fontSize: '12px',
                  color: '#80868b'
                }}>
                  <Clock className="w-3 h-3" />
                  <span>Updated {supplier.lastUpdate}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ 
              display: 'flex', 
              gap: '8px',
              marginTop: '8px'
            }}>
              {supplier.status === 'wrong-site' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(supplier.id, 'directions');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#d93025',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b7241c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#d93025';
                  }}
                >
                  <Send className="w-4 h-4" />
                  Send Directions
                </button>
              )}
              
              {!supplier.isSharing && supplier.status === 'unknown' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(supplier.id, 'request-location');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1b5e20';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#2e7d32';
                  }}
                >
                  <MapPin className="w-4 h-4" />
                  Request Location
                </button>
              )}
              
              {supplier.phone && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(supplier.id, 'call');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#fff',
                    color: '#5f6368',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                >
                  <Phone className="w-4 h-4" />
                  Call
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      )}
      </div>
    </div>
  );
};